import { Modal } from '@/components/custom/modal';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { FiUpload, FiX } from 'react-icons/fi';
import { CardIcon } from '@/components/ui/card';
import clsx from 'clsx';

export default function ImportModal({ show, closeModal, courses, regionals, classrooms, getSpreadsheets }) {
  const client = useClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const fileInputRef = useRef(null);

  const importClassForm = useFormik({
    initialValues: {
      regional: '',
      school: '',
      course: '',
      classroom: '',
      spreadsheet: ''
    }
  });

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const getInstitutionsByRegional = async regionalId => {
    setLoadingInstitutions(true);
    const { data, error } = await client.fetch(`institutions?filter[regional_id]=${regionalId}`);
    if (error) {
      toast.error('Erro ao buscar escolas.');
    } else {
      setFilteredInstitutions(data);
    }
    setLoadingInstitutions(false);
  };

  useEffect(() => {
    if (importClassForm.values.regional) {
      getInstitutionsByRegional(importClassForm.values.regional);
    } else {
      setFilteredInstitutions([]);
    }
    importClassForm.setFieldValue('school', '');
  }, [importClassForm.values.regional]);

  useEffect(() => {
    importClassForm.setFieldValue('course', '');
  }, [importClassForm.values.school]);

  useEffect(() => {
    importClassForm.setFieldValue('classroom', '');
  }, [importClassForm.values.course]);

  useEffect(() => {
    if (!show) {
      importClassForm.resetForm();
      setFilteredInstitutions([]);
    }
  }, [show]);

  const filteredClassrooms = classrooms.filter(item => {
    return item?.['institution-id'] === parseInt(importClassForm.values.school) && item?.['course-id'] === parseInt(importClassForm.values.course);
  });

  const sendSpreadsheet = async () => {
    if (importClassForm.values.spreadsheet && importClassForm.values.spreadsheet !== '') {
      const requestParams = {
        file: await toBase64(importClassForm.values.spreadsheet),
        filename: importClassForm.values.spreadsheet.name,
        classroom_id: importClassForm.values.classroom
      };
      const { error } = await client.mutate('spreadsheet_imports', requestParams);
      if (error) {
        toast.error('Erro ao enviar planilha.');
      } else {
        toast.success('Planilha enviada!');
        closeModal();
        getSpreadsheets();
      }
    }
  };

  return (
    <Modal
      show={show}
      onClose={closeModal}
    >
      <form
        className="form"
        onSubmit={e => e.preventDefault()}
      >
        <h2 className="modal__simple-title">{t('classes.importTitle')}</h2>

        <p className="form__description text-sm">{t('classes.importDescription')}</p>

        <div className="form__row">
          <label
            className="form__label"
            htmlFor="regional"
          >
            GRE
          </label>
          <select
            className="form__select"
            name="regional"
            id="regional"
            onChange={e => importClassForm.setFieldValue('regional', e.target.value)}
            value={importClassForm?.values?.regional}
          >
            <option value="">Selecione a regional</option>
            {regionals.map(regional => (
              <option
                key={regional.id}
                value={regional.id}
              >
                {regional.name}
              </option>
            ))}
          </select>
          {importClassForm.touched.regional && importClassForm.errors.regional && <span style={{ color: 'red' }}>{importClassForm.errors.regional}</span>}
        </div>

        <div className="form__row">
          <label
            className="form__label"
            htmlFor="school"
          >
            *{t('textsCommon.school')}
          </label>
          <select
            className="form__select"
            name="school"
            id="school"
            disabled={!importClassForm.values.regional || loadingInstitutions}
            onChange={e => importClassForm.setFieldValue('school', e.target.value)}
            value={importClassForm?.values?.school}
          >
            <option value="">{loadingInstitutions ? 'Carregando...' : filteredInstitutions.length > 0 ? 'Selecione a escola' : 'Não há registro de cadastro'}</option>
            {filteredInstitutions.map(institution => (
              <option
                key={institution.id}
                value={institution.id}
              >
                {institution.name}
              </option>
            ))}
          </select>
          {importClassForm.touched.school && importClassForm.errors.school && <span style={{ color: 'red' }}>{importClassForm.errors.school}</span>}
        </div>

        <div className="form__row">
          <label
            className="form__label"
            htmlFor="course"
          >
            *{t('textsCommon.course')}
          </label>
          <select
            className="form__select"
            name="course"
            id="course"
            disabled={!importClassForm.values.school}
            onChange={e => importClassForm.setFieldValue('course', e.target.value)}
            value={importClassForm?.values?.course || ''}
          >
            <option value="">{courses.length > 0 ? t('exams.selectCourse') : 'Não há registro de cadastro'}</option>
            {courses.map(course => (
              <option
                key={course.id}
                value={course.id}
              >
                {course.title}
              </option>
            ))}
          </select>
          {importClassForm.touched.course && importClassForm.errors.course && <span style={{ color: 'red' }}>{importClassForm.errors.course}</span>}
        </div>
        <div className="form__row">
          <label
            className="form__label"
            htmlFor="classroom"
          >
            *{t('textsCommon.classroom')}
          </label>
          <select
            className="form__select"
            name="classroom"
            id="classroom"
            disabled={!importClassForm.values.course}
            onChange={e => importClassForm.setFieldValue('classroom', e.target.value)}
            value={importClassForm?.values?.classroom || ''}
          >
            <option value="">{filteredClassrooms.length > 0 ? t('createAccount.selectClass') : 'Não há registro de cadastro'}</option>
            {filteredClassrooms.map(classroom => (
              <option
                key={classroom.id}
                value={classroom.id}
              >
                {classroom.title}
              </option>
            ))}
          </select>
          {importClassForm.touched.classroom && importClassForm.errors.classroom && <span style={{ color: 'red' }}>{importClassForm.errors.classroom}</span>}
        </div>

        <input
          type="file"
          name="file"
          id="file"
          className="hidden"
          accept=".csv, .xlsx"
          ref={fileInputRef}
          onChange={e => {
            importClassForm.setFieldValue('spreadsheet', e.target.files[0]);
          }}
        />

        {importClassForm.values.spreadsheet && (
          <div className="mb-3 flex items-center justify-between gap-2 rounded border border-neutral-lightest bg-white p-1">
            <div className="flex w-full min-w-0 items-center justify-between gap-1 overflow-hidden text-sm text-neutral">
              <div className={clsx('flex min-w-0 flex-1 items-center gap-1.5')}>
                <span className="min-w-0 max-w-96 overflow-hidden text-ellipsis text-nowrap">{importClassForm.values.spreadsheet.name}</span>
              </div>
              <CardIcon>
                <FiX
                  className="shrink-0 cursor-pointer text-lg text-primary"
                  onClick={() => {
                    importClassForm.setFieldValue('spreadsheet', '');
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                />
              </CardIcon>
            </div>
          </div>
        )}

        <button
          className="btn btn--primary w-full"
          type="button"
          disabled={isSubmitting || !importClassForm.values.classroom}
          onClick={() => {
            if (importClassForm.values.spreadsheet === '') {
              fileInputRef.current.click();
            } else {
              sendSpreadsheet();
            }
          }}
        >
          <FiUpload className="h-5 w-5" />
          {importClassForm.values.spreadsheet ? t('classes.buttonImportSpreadsheet') : t('classes.buttonSelectFile')}
        </button>
        <p className="mt-2 text-center text-sm text-neutral">Formatos aceitos: .xlsx, .csv</p>
      </form>
    </Modal>
  );
}
